import { useEffect, useState } from "react";
import {
  useCreateSchedule,
  useDeleteSchedule,
  useSchedulePresets,
  useSchedulerStatus,
  useTestSchedule,
  useUpdateSchedule,
  type Schedule,
} from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Field } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { Select, Toggle } from "@/components/ui/Select";
import { AlertIcon, ClockIcon, MailIcon } from "@/components/icons";

/**
 * Creating and editing a schedule (PRD FR-8.1, FR-8.2).
 *
 * Presets first, raw cron behind a disclosure. The preset is what an operator
 * wants nine times out of ten, and the tenth is an admin who knows what
 * `0 6 * * 1-5` means — both compile to the same stored expression, so a
 * schedule reads the same way however it was created.
 *
 * The timezone is stated, never chosen. `MING_TIMEZONE` drives cron, the as-of
 * stamps and the dates in the prompt; a per-schedule timezone would make "this
 * ran at 07:00" mean two different things in one deployment.
 */

type Preset = "daily" | "weekdays" | "weekly" | "monthly";

interface Draft {
  preset: Preset | "";
  hour: number;
  minute: number;
  weekday: number;
  day: number;
  cron: string;
  recipients: string;
  subject: string;
  enabled: boolean;
  attachPdf: boolean;
  includeSql: boolean;
}

function draftOf(schedule: Schedule | null): Draft {
  if (!schedule) {
    return {
      preset: "daily",
      hour: 7,
      minute: 0,
      weekday: 0,
      day: 1,
      cron: "",
      recipients: "",
      subject: "",
      enabled: true,
      attachPdf: true,
      includeSql: false,
    };
  }
  // Cron is the stored form for presets too, so the editor reads its fields
  // back out of the expression rather than keeping a second copy of them.
  const [minute = "0", hour = "7", day = "1", , weekday = "0"] = schedule.cron.split(" ");
  return {
    preset: (schedule.preset as Preset | null) ?? "",
    hour: Number.parseInt(hour, 10) || 7,
    minute: Number.parseInt(minute, 10) || 0,
    weekday: (Number.parseInt(weekday, 10) + 6) % 7 || 0,
    day: Number.parseInt(day, 10) || 1,
    cron: schedule.preset ? "" : schedule.cron,
    recipients: schedule.recipients.join(", "),
    subject: schedule.subject,
    enabled: schedule.enabled,
    attachPdf: schedule.attach_pdf,
    includeSql: schedule.include_sql_appendix,
  };
}

function splitRecipients(value: string): string[] {
  return value
    .split(/[,;\s]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

export function ScheduleDialog({
  open,
  onClose,
  ownerKind,
  ownerId,
  schedule,
}: {
  open: boolean;
  onClose: () => void;
  ownerKind: "report" | "dashboard";
  ownerId: string;
  schedule: Schedule | null;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(schedule));
  const [failure, setFailure] = useState<string | null>(null);
  const [custom, setCustom] = useState(Boolean(schedule && !schedule.preset));
  const { data: presets } = useSchedulePresets();
  const { data: status } = useSchedulerStatus();
  const create = useCreateSchedule();
  const update = useUpdateSchedule(schedule?.id ?? "");
  const remove = useDeleteSchedule();
  const test = useTestSchedule();
  const [tested, setTested] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(draftOf(schedule));
      setCustom(Boolean(schedule && !schedule.preset));
      setFailure(null);
      setTested(null);
      setConfirmingDelete(false);
    }
  }, [open, schedule]);

  const body = {
    preset: custom ? null : (draft.preset as Preset),
    hour: draft.hour,
    minute: draft.minute,
    weekday: draft.weekday,
    day: draft.day,
    cron: custom ? draft.cron : "",
    recipients: splitRecipients(draft.recipients),
    subject: draft.subject,
    enabled: draft.enabled,
    attach_pdf: draft.attachPdf,
    include_sql_appendix: draft.includeSql,
  };

  async function save() {
    setFailure(null);
    try {
      if (schedule) {
        await update.mutateAsync(body);
      } else {
        await create.mutateAsync({ owner_kind: ownerKind, owner_id: ownerId, name: "", ...body });
      }
      onClose();
    } catch (error) {
      setFailure(error instanceof Error ? error.message : "That schedule could not be saved.");
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={schedule ? "Edit schedule" : "Schedule this report"}
        description={
          status?.timezone
            ? `Runs in the deployment timezone, ${status.timezone}.`
            : "Runs in the deployment timezone."
        }
        footer={
          <div className="flex flex-wrap items-center gap-2">
            {schedule ? (
              <Button
                icon={<MailIcon className="size-4" />}
                loading={test.isPending}
                onClick={() =>
                  test.mutate(schedule.id, {
                    onSuccess: (result) =>
                      setTested(
                        result.status === "sent"
                          ? `Sent to ${result.recipients.join(", ")}.`
                          : `${result.status}: ${result.error_message ?? result.error_code ?? ""}`,
                      ),
                  })
                }
              >
                Send a test
              </Button>
            ) : null}
            {schedule ? (
              <Button onClick={() => setConfirmingDelete(true)}>Delete</Button>
            ) : null}
            <span className="flex-1" />
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={create.isPending || update.isPending} onClick={() => void save()}>
              {schedule ? "Save" : "Create schedule"}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {!custom ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="How often"
                value={draft.preset}
                onChange={(event) =>
                  setDraft({ ...draft, preset: event.target.value as Preset })
                }
              >
                {(presets?.presets ?? []).map((preset) => (
                  <option key={preset.preset} value={preset.preset}>
                    {preset.description}
                  </option>
                ))}
              </Select>
              <Field
                label="At"
                type="time"
                value={`${String(draft.hour).padStart(2, "0")}:${String(draft.minute).padStart(2, "0")}`}
                onChange={(event) => {
                  const [hour = "0", minute = "0"] = event.target.value.split(":");
                  setDraft({
                    ...draft,
                    hour: Number.parseInt(hour, 10) || 0,
                    minute: Number.parseInt(minute, 10) || 0,
                  });
                }}
              />
              {draft.preset === "weekly" ? (
                <Select
                  label="On"
                  value={String(draft.weekday)}
                  onChange={(event) =>
                    setDraft({ ...draft, weekday: Number.parseInt(event.target.value, 10) })
                  }
                >
                  {(presets?.weekdays ?? []).map((name, index) => (
                    <option key={name} value={index}>
                      {name}
                    </option>
                  ))}
                </Select>
              ) : null}
              {draft.preset === "monthly" ? (
                <Field
                  label="Day of the month"
                  type="number"
                  min={1}
                  max={28}
                  value={draft.day}
                  onChange={(event) =>
                    setDraft({ ...draft, day: Number.parseInt(event.target.value, 10) || 1 })
                  }
                  hint="Capped at 28, so a monthly report exists in February."
                />
              ) : null}
            </div>
          ) : (
            <Field
              label="Cron expression"
              value={draft.cron}
              placeholder="0 7 * * 1-5"
              onChange={(event) => setDraft({ ...draft, cron: event.target.value })}
              hint={`Five fields: minute hour day month weekday, in ${status?.timezone ?? "the deployment timezone"}.`}
            />
          )}

          <button
            type="button"
            onClick={() => setCustom((value) => !value)}
            className="type-caption text-accent self-start normal-case hover:underline"
          >
            {custom ? "Use a preset instead" : "Write a cron expression instead"}
          </button>

          <Field
            label="Email to"
            value={draft.recipients}
            placeholder="team@example.com, ops@example.com"
            onChange={(event) => setDraft({ ...draft, recipients: event.target.value })}
            hint={
              status && !status.email_configured
                ? "No SMTP server is configured, so this will refresh and snapshot without sending."
                : "Comma-separated. Leave empty to refresh and snapshot without emailing anyone."
            }
          />

          <Field
            label="Subject"
            value={draft.subject}
            placeholder="Left empty: the report's title and the date"
            onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
          />

          <Toggle
            label="Attach the PDF"
            checked={draft.attachPdf}
            onChange={(value) => setDraft({ ...draft, attachPdf: value })}
            hint={
              status?.pdf_renderer === "none"
                ? "This deployment has no PDF renderer, so the email will carry the figures only."
                : "The email carries the headline figures inline; the PDF carries the whole report."
            }
          />
          {/* The appendix is part of the PDF and nothing else reads it
              (`scheduler._render` passes it to the renderer inside the
              attach_pdf branch), so with the attachment off this setting is
              inert. Disabled rather than hidden, and the stored value is kept:
              turning the PDF back on restores the choice instead of silently
              losing it. */}
          <Toggle
            label="Append the queries used"
            checked={draft.includeSql}
            disabled={!draft.attachPdf}
            onChange={(value) => setDraft({ ...draft, includeSql: value })}
            hint={
              draft.attachPdf
                ? "Adds every statement behind the report to the end of the PDF."
                : "The appendix is part of the PDF. Attach it above to include the queries."
            }
          />
          <Toggle
            label="Enabled"
            checked={draft.enabled}
            onChange={(value) => setDraft({ ...draft, enabled: value })}
            hint="A disabled schedule keeps its settings and its history and fires nothing."
          />

          {status && !status.running && status.enabled ? (
            <p className="type-support text-warning flex items-start gap-2">
              <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
              <span>The scheduler is not running in this process, so nothing will fire.</span>
            </p>
          ) : null}

          {tested ? (
            <p className="type-support text-ink-secondary flex items-start gap-2">
              <ClockIcon className="mt-0.5 size-3.5 shrink-0" />
              <span>{tested}</span>
            </p>
          ) : null}

          {failure ? (
            <p className="bg-error-subtle text-error type-support rounded-element px-3 py-2">
              {failure}
            </p>
          ) : null}

          {schedule?.next_fire_at ? (
            <Chip tone="neutral">
              next: {new Date(schedule.next_fire_at).toLocaleString()}
            </Chip>
          ) : null}
        </div>
      </Modal>

      {/* A sibling of the dialog rather than a child of it: `showModal()` puts
          this on the top layer above the schedule dialog, which is what makes
          the one underneath inert while the question is being answered. */}
      <ConfirmDialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        loading={remove.isPending}
        title="Delete this schedule?"
        description={
          <>
            The {schedule?.description ? `“${schedule.description}” ` : ""}schedule on this{" "}
            {ownerKind} will be deleted.
          </>
        }
        consequence={
          schedule?.recipients?.length
            ? `Nothing further will be sent to ${schedule.recipients.join(", ")}, and this ${ownerKind} stops refreshing and snapshotting on its own. The snapshots already taken are kept.`
            : `This ${ownerKind} stops refreshing and snapshotting on its own. The snapshots already taken are kept.`
        }
        onConfirm={() => {
          if (!schedule) return;
          remove.mutate(schedule.id, {
            onSuccess: () => {
              setConfirmingDelete(false);
              onClose();
            },
          });
        }}
      />
    </>
  );
}
